import Vue from 'vue';
import find from 'lodash/find';
import { assert } from '../../utils/assert';
import { getPanelElementId } from './helpers';

export default {
  methods: {
    /**
     * Gets any active item on the stack for the given key and id.
     *
     * As they share their data this should not really matter.
     * The only difference would be their "view state" (tabs open, etc.).
     *
     * @param key
     * @returns {Object|null}
     */
    getActiveStackItem(key, id) {
      const stack = this.$stack;
      return stack.$store ? stack.$store.getters.fetch(key, id) : null;
    },

    /**
     * Fetches the next item in the stack.
     * If the data parameter is set, the data will be returned (if any).
     *
     * @param key
     * @param data
     * @returns {Object|null}
     */
    getNextStackItem(data = false) {
      const stack = this.$stack;

      if (stack.$store) {
        const index = this.getIndexInItemStack();
        const panels = stack.$store.getters.items;
        const panel = panels[index + 1];

        if (panel) {
          return data ? panel.data : panel;
        }
      }

      return null;
    },

    /**
     * Gets the ID of the next stack item if it matches the given key.
     * Returns -1 otherwise.
     *
     * @param key
     * @param id
     * @return {Number}
     */
    getNextStackItemId(key) {
      assert(key, 'key mising from next stack request');

      const panel = this.getNextStackItem();
      return panel && panel.key === key ? panel.id : -1;
    },

    /**
     * Checks if the next item in the stack has the given key and ID.
     *
     * @param key
     * @param id
     * @return {Boolean}
     */
    hasNextStackItem(key, id) {
      const panel = this.getNextStackItem();
      return panel && panel.id === id && panel.key === key;
    },

    /**
     * Runs the callback over the given collection
     * for all active items defind by the key.
     *
     * @param key
     * @param collection
     * @param callback
     * @return {Object|null}
     */
    findActiveStackItem(key, collection, callback = 'id') {
      const stack = this.$stack;

      if (stack.$store) {
        const active = stack.$store.getters.active[key];
        const id = callback;

        if (typeof callback === 'string') {
          callback = () => (entry => (!!active[entry[id]]));
        }

        return active ? find(collection, callback(active)) : undefined;
      }

      return null;
    },

    getParentStackViewData(key = null, fallback = null) {
      if (this.$stack !== this) {
        for (let vue = this; vue !== this.$root; vue = vue.$parent) {
          if (vue.$vnode.data.stackPanel !== undefined) {
            const { stackPanel } = vue.$vnode.data;
            return key ? stackPanel[key] : stackPanel;
          }
        }
      }

      return fallback;
    },

    /**
     * Gets the type of the current (parent) panel in the stack.
     * Returns null if we're in the main panel (Not an actual extension).
     *
     * @return {String}
     */
    getTypeInItemStack() {
      return this.getParentStackViewData('type');
    },

    /**
     * Gets the object ID of the current (parent) panel in the stack.
     * Returns -1 if we're in the main panel (Not an actual extension).
     *
     * @return {String}
     */
    getIdInItemStack() {
      return this.getParentStackViewData('id');
    },

    /**
     * Gets the index of the current (parent) panel in the stack.
     * Returns -1 if we're in the main panel (Not an actual extension).
     *
     * @return {number}
     */
    getIndexInItemStack() {
      return this.getParentStackViewData('index', -1);
    },

    /**
     * Gets the uuid of the current (parent) panel in the stack.
     * Returns -1 if we're in the main panel (Not an actual extension).
     *
     * @return {number}
     */
    getUuidInItemStack() {
      return this.getParentStackViewData('uuid', -1);
    },

    /**
     * Tries to display the given item in the stack.
     *
     * @param key
     * @param id
     */
    displayStackItem(key, id) {
      const store = this.$stack.$store;
      assert(store, `No flex stack in use, cannot display item ${key}:${id}`);

      // Figure out where in the item list we are
      const index = this.getIndexInItemStack() + 1;

      // Update contents
      store.dispatch('display', {
        key, id, index,
      });

      // Try to scroll into view
      const elementId = getPanelElementId(store.getters.panels[index]);
      Vue.nextTick(() => document.getElementById(elementId).scrollIntoView());
    },

    /**
     * Tries to refresh the current item in the stack.
     *
     * @param key
     * @param id
     * @param strict
     */
    refreshStackItem() {
      // Figure out where in the item list we are
      const store = this.$stack.$store;
      const { id, type: key, index } = this.getParentStackViewData();

      // Refresh contents
      store.dispatch('display', {
        key, id, index,
      });
    },
  },
};



// WEBPACK FOOTER //
// ./resources/assets/js/view-stack/utils/mixin.js